import { useMemo } from 'react';
import { useHyperliquidStore } from '@/stores/hyperliquidStore';

export interface OrderLevel {
  price: number;
  size: number;
  n: number;
}

export interface OrderLevelWithTotal extends OrderLevel {
  accumulativeSize: number;
  accumulativeUSD: number;
}

export interface Trade {
  time: number;
  px: string;
  sz: string;
  side: 'A' | 'B';
}

export function useOrderBook() {
  // Store data
  const bids = useHyperliquidStore(state => state.orderBook.bids);
  const asks = useHyperliquidStore(state => state.orderBook.asks);
  const trades = useHyperliquidStore(state => state.trades);

  // Calculate cumulative totals for orderbook
  const asksWithTotal = useMemo(() => {
    return asks.slice(0, 30).map((ask, i, arr) => {
      const cumulativeSize = arr.slice(0, i + 1).reduce((sum, a) => sum + parseFloat(a.sz), 0);
      const price = parseFloat(ask.px);
      const size = parseFloat(ask.sz);
      return {
        price: price,
        size: size,
        usd: price * size,
        accumulativeSize: cumulativeSize,
        accumulativeUSD: cumulativeSize * price,
        orders: ask.n,
      };
    });
  }, [asks]);

  const bidsWithTotal = useMemo(() => {
    return bids.slice(0, 30).map((bid, i, arr) => {
      const cumulativeSize = arr.slice(0, i + 1).reduce((sum, b) => sum + parseFloat(b.sz), 0);
      const price = parseFloat(bid.px);
      const size = parseFloat(bid.sz);

      return {
        price: price,
        size: size,
        usd: price * size,
        accumulativeSize: cumulativeSize,
        accumulativeUSD: cumulativeSize * price,
        orders: bid.n,
      };
    });
  }, [bids]);

  // Calculate max total for depth visualization
  const maxTotal = useMemo(() => {
    return Math.max(...asksWithTotal.map(a => a.accumulativeUSD), ...bidsWithTotal.map(b => b.accumulativeUSD), 1);
  }, [asksWithTotal, bidsWithTotal]);

  // Format numbers
  const formatNumber = (num: number, decimals: number = 0) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  };

  // Format time for trades
  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // Calculate spread
  const bestBid = bids[0]?.px ? parseFloat(bids[0].px) : 0;
  const bestAsk = asks[0]?.px ? parseFloat(asks[0].px) : 0;
  const spread = bestAsk - bestBid;
  const spreadPercent = bestBid > 0 ? (spread / bestBid) * 100 : 0;

  return {
    // Data
    bids,
    asks,
    trades,
    asksWithTotal,
    bidsWithTotal,
    maxTotal,

    // Spread calculations
    bestBid,
    bestAsk,
    spread,
    spreadPercent,

    // Utility functions
    formatNumber,
    formatTime,
  };
}
